import { useState } from "react";
import { initialsOf } from "@worktools/shared";
import { api, type BoardMeta } from "../api/client";
import type { Session } from "../discord/session";
import type { LiveBoard } from "../sync/lobby";
import { useDialogs } from "../ui/dialogs";
import { IconBoards, IconPlus } from "./icons";
import "../styles/board.css";
import "../styles/landing.css";

export function Landing({
  session,
  mine,
  shared,
  liveBoards,
  onOpenBoard,
  onRequestJoin,
  onBoardsChanged,
}: {
  session: Session;
  mine: BoardMeta[];
  shared: BoardMeta[];
  liveBoards: LiveBoard[];
  onOpenBoard: (id: string) => void;
  onRequestJoin: (id: string) => void;
  onBoardsChanged: () => Promise<unknown>;
}) {
  const dialogs = useDialogs();
  const [busy, setBusy] = useState(false);

  const createBoard = async () => {
    const name = await dialogs.prompt({
      title: "Neues Board",
      label: "Wie soll das Board heißen?",
      value: `Board ${mine.length + 1}`,
      confirmLabel: "Anlegen",
    });
    if (!name) return;
    setBusy(true);
    try {
      const { board } = await api.createBoard({
        name,
        channelId: session.channelId,
        guildId: session.guildId,
      });
      await onBoardsChanged();
      onOpenBoard(board.id);
    } finally {
      setBusy(false);
    }
  };

  const otherLive = liveBoards.filter((live) => live.owner.id !== session.user.id);

  return (
    <div className="landing">
      <header className="landing__bar">
        <img className="header__mark" src="/artim-logo.png" alt="Artim Industries" />
        <div className="header__title">
          <b>Worktools</b>
          <span>{session.user.name}</span>
        </div>
        <div className="header__spacer" />
        <button className="hbtn hbtn--accent" onClick={createBoard} disabled={busy}>
          <IconPlus size={14} /> Neues Board
        </button>
      </header>

      <div className="landing__body scroll">
        <section className="landing__section">
          <div className="landing__heading">
            <h2>Gerade live</h2>
            <span className="label">in diesem Channel</span>
          </div>
          {otherLive.length === 0 ? (
            <p className="landing__empty">
              Niemand teilt gerade ein Board. Wenn du eins öffnest, kannst du es über
              „Mit Channel teilen“ für alle hier sichtbar machen.
            </p>
          ) : (
            <div className="cards">
              {otherLive.map((live) => (
                <article key={live.boardId} className="card card--live">
                  <div className="card__live">
                    <span className="pulse" />
                    Live
                  </div>
                  <h3>{live.boardName}</h3>
                  <div className="card__owner">
                    <span className="avatar avatar--static" style={{ borderColor: "var(--accent)" }}>
                      {live.owner.avatarUrl ? (
                        <img src={live.owner.avatarUrl} alt="" />
                      ) : (
                        initialsOf(live.owner.name)
                      )}
                    </span>
                    <span>
                      {live.owner.name} arbeitet hier · {live.participants}{" "}
                      {live.participants === 1 ? "Person" : "Personen"}
                    </span>
                  </div>
                  {live.joined ? (
                    <button className="pill pill--solid" onClick={() => onOpenBoard(live.boardId)}>
                      Öffnen
                    </button>
                  ) : live.pending ? (
                    <button className="pill" disabled>
                      Anfrage gesendet…
                    </button>
                  ) : (
                    <button className="pill pill--solid" onClick={() => onRequestJoin(live.boardId)}>
                      Beitritt anfragen
                    </button>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>

        <BoardSection
          title="Meine Boards"
          hint="gehören dir"
          boards={mine}
          liveBoards={liveBoards}
          onOpenBoard={onOpenBoard}
          emptyText="Noch kein Board. Leg dein erstes an."
        />

        <BoardSection
          title="Mit mir geteilt"
          hint="dauerhaft freigegeben"
          boards={shared}
          liveBoards={liveBoards}
          onOpenBoard={onOpenBoard}
          emptyText="Hier taucht auf, was andere dauerhaft mit dir teilen."
        />
      </div>
    </div>
  );
}

function BoardSection({
  title,
  hint,
  boards,
  liveBoards,
  onOpenBoard,
  emptyText,
}: {
  title: string;
  hint: string;
  boards: BoardMeta[];
  liveBoards: LiveBoard[];
  onOpenBoard: (id: string) => void;
  emptyText: string;
}) {
  return (
    <section className="landing__section">
      <div className="landing__heading">
        <h2>{title}</h2>
        <span className="label">{hint}</span>
      </div>
      {boards.length === 0 ? (
        <p className="landing__empty">{emptyText}</p>
      ) : (
        <div className="cards">
          {boards.map((board) => {
            const live = liveBoards.some((entry) => entry.boardId === board.id);
            return (
              <button key={board.id} className="card card--action" onClick={() => onOpenBoard(board.id)}>
                <div className="card__icon">
                  <IconBoards size={18} />
                </div>
                <h3>{board.name}</h3>
                <div className="card__meta">
                  {board.role === "owner" ? "Dein Board" : `von ${board.ownerName || "unbekannt"}`}
                  {board.sharedWith.length > 0 && ` · ${board.sharedWith.length} freigegeben`}
                </div>
                {live && (
                  <div className="card__live card__live--small">
                    <span className="pulse" />
                    Live
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
